import React from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import type { JobApplicationFlowStage, SankeyLinkTooltipMeta, SankeyNodeTooltipMeta } from '../types/visualization';
import { Msg, useFormatMessage } from '../i18n-review/client/Msg';

const stageHints = defineMessages({
  browsed: {
    id: 'tools.jobFlowSankey.tooltip.hint.browsed',
    defaultMessage: 'Roles you opened from search — the start of your funnel.',
    description: 'Sankey node tooltip: browsed stage',
  },
  viewed: {
    id: 'tools.jobFlowSankey.tooltip.hint.viewed',
    defaultMessage: 'You opened these listings but have not saved them yet.',
    description: 'Sankey node tooltip: viewed stage',
  },
  saved: {
    id: 'tools.jobFlowSankey.tooltip.hint.saved',
    defaultMessage: 'Saved for later — easy to come back and apply.',
    description: 'Sankey node tooltip: saved stage',
  },
  applied: {
    id: 'tools.jobFlowSankey.tooltip.hint.applied',
    defaultMessage: 'You applied or tracked these as submitted applications.',
    description: 'Sankey node tooltip: applied stage',
  },
  interviewing: {
    id: 'tools.jobFlowSankey.tooltip.hint.interviewing',
    defaultMessage: 'Currently in the interview process.',
    description: 'Sankey node tooltip: interviewing',
  },
  offer: {
    id: 'tools.jobFlowSankey.tooltip.hint.offer',
    defaultMessage: 'Offer stage — negotiation or decision time.',
    description: 'Sankey node tooltip: offer',
  },
  rejected: {
    id: 'tools.jobFlowSankey.tooltip.hint.rejected',
    defaultMessage: 'Marked closed or not moving forward.',
    description: 'Sankey node tooltip: rejected',
  },
  hired: {
    id: 'tools.jobFlowSankey.tooltip.hint.hired',
    defaultMessage: 'Marked hired — great outcome.',
    description: 'Sankey node tooltip: hired',
  },
});

export type JobFlowSankeyTooltipPayload =
  | {
      kind: 'node';
      displayName: string;
      meta: SankeyNodeTooltipMeta;
      accentColor: string;
    }
  | {
      kind: 'link';
      fromName: string;
      toName: string;
      meta: SankeyLinkTooltipMeta;
      accentColor: string;
    };

export const JobFlowSankeyTooltipContent: React.FC<{ payload: JobFlowSankeyTooltipPayload }> = ({
  payload,
}) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { accentColor } = payload;

  if (payload.kind === 'node') {
    const { displayName, meta } = payload;
    const hint = stageHints[meta.stage];
    return (
      <div className="max-w-[min(100vw-2rem,18rem)]">
        <div className="h-1 w-10 rounded-full mb-2" style={{ background: accentColor }} aria-hidden />
        <p className="text-base font-bold text-[#1E1A14] dark:text-[#F2EADF] leading-snug">{displayName}</p>
        <p className="mt-2 text-sm font-semibold text-[#3D3529] dark:text-[#C4A882] tabular-nums">
          <Msg
            id="tools.jobFlowSankey.tooltip.node.jobCount"
            defaultMessage="{count, plural, one {# job} other {# jobs}}"
            description="Sankey tooltip: job count for a stage"
            values={{ count: meta.jobCount }}
          />
        </p>
        {meta.shareOfBrowsedPct != null ? (
          <p className="mt-1 text-xs text-[#5A4A3A] dark:text-[#B8A48C]">
            <Msg
              id="tools.jobFlowSankey.tooltip.node.pctBrowsed"
              defaultMessage="{pct}% of listings you browsed"
              description="Sankey tooltip: share of browsed"
              values={{ pct: meta.shareOfBrowsedPct }}
            />
          </p>
        ) : null}
        {meta.shareOfAppliedPct != null ? (
          <p className="mt-1 text-xs text-[#5A4A3A] dark:text-[#B8A48C]">
            <Msg
              id="tools.jobFlowSankey.tooltip.node.pctApplied"
              defaultMessage="{pct}% of your applications"
              description="Sankey tooltip: share of applied pool"
              values={{ pct: meta.shareOfAppliedPct }}
            />
          </p>
        ) : null}
        {meta.appliedBeyondBrowse ? (
          <p className="mt-1 text-xs font-medium text-[#6E4F00] dark:text-[#D4A820]">
            <Msg
              id="tools.jobFlowSankey.tooltip.node.beyondBrowse"
              defaultMessage="Includes applications not counted in browse (e.g. off-site)."
              description="Sankey tooltip: applied exceeds browsed"
            />
          </p>
        ) : null}
        <p className="mt-3 text-xs leading-relaxed text-[#7A6E5F] dark:text-[#B8A48C] border-t border-[#DDD5C8]/80 dark:border-[#4A3520]/80 pt-2">
          {formatMessage(hint)}
        </p>
      </div>
    );
  }

  const { fromName, toName, meta } = payload;
  const showSplit =
    meta.fromStage === 'browsed' &&
    meta.toStage === 'applied' &&
    ((meta.fromBrowsedDirect ?? 0) > 0 || (meta.fromOutsideFunnel ?? 0) > 0);

  return (
    <div className="max-w-[min(100vw-2rem,20rem)]">
      <div className="h-1 w-10 rounded-full mb-2" style={{ background: accentColor }} aria-hidden />
      <p className="text-sm font-bold text-[#1E1A14] dark:text-[#F2EADF] leading-snug">
        <span className="text-[#3B5E45] dark:text-[#98CFA8]">{fromName}</span>
        <span className="mx-1.5 text-[#7A6E5F] dark:text-[#B8A48C] font-normal" aria-hidden>
          →
        </span>
        <span className="text-[#3B5E45] dark:text-[#98CFA8]">{toName}</span>
      </p>
      <p className="mt-2 text-sm font-semibold text-[#3D3529] dark:text-[#C4A882] tabular-nums">
        <Msg
          id="tools.jobFlowSankey.tooltip.link.pathCount"
          defaultMessage="{count, plural, one {# job} other {# jobs}} on this path"
          description="Sankey link tooltip: count"
          values={{ count: meta.jobCount }}
        />
      </p>
      {meta.pctOfFromOutflow != null ? (
        <p className="mt-1 text-xs text-[#5A4A3A] dark:text-[#B8A48C]">
          <Msg
            id="tools.jobFlowSankey.tooltip.link.pctFrom"
            defaultMessage="{pct}% of flow leaving {fromName}"
            description="Sankey link tooltip: share leaving source"
            values={{ pct: meta.pctOfFromOutflow, fromName }}
          />
        </p>
      ) : null}
      {meta.pctOfToInflow != null ? (
        <p className="mt-1 text-xs text-[#5A4A3A] dark:text-[#B8A48C]">
          <Msg
            id="tools.jobFlowSankey.tooltip.link.pctTo"
            defaultMessage="{pct}% of flow into {toName}"
            description="Sankey link tooltip: share entering target"
            values={{ pct: meta.pctOfToInflow, toName }}
          />
        </p>
      ) : null}
      {showSplit ? (
        <p className="mt-2 text-xs leading-relaxed text-[#7A6E5F] dark:text-[#B8A48C] border-t border-[#DDD5C8]/80 dark:border-[#4A3520]/80 pt-2">
          <Msg
            id="tools.jobFlowSankey.tooltip.link.splitBrowsedApplied"
            defaultMessage="{direct} from browse without save · {external} from elsewhere"
            description="Sankey link tooltip: browsed to applied breakdown"
            values={{
              direct: meta.fromBrowsedDirect ?? 0,
              external: meta.fromOutsideFunnel ?? 0,
            }}
          />
        </p>
      ) : null}
    </div>
  );
};
